import {useCallback, useEffect, useRef, useState} from 'react';
import type {VisibleRange} from '@/components/app/data-grid';
export interface UseLazyLoadOptions<TItem, TVirtualItem = TItem> {
  getItems: (options: {pageNumber: number; pageSize: number}) => Promise<TItem[]>;
  getItemsCount: () => Promise<number>;
  pageSize: number;
  skip?: boolean;
  virtualItem: TVirtualItem | ((data: {index: number}) => TVirtualItem) | null;
}
export function useVirtualLazyLoad<TItem, TVirtualItem = TItem>({
  getItems,
  getItemsCount,
  pageSize,
  skip,
  virtualItem,
}: UseLazyLoadOptions<TItem, TVirtualItem>) {
  const [items, setItems] = useState<(TItem | TVirtualItem)[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [loadingCount, setLoadingCount] = useState(0);
  const [error, setError] = useState<unknown>(null);
  const generation = useRef(0);
  const loaded = useRef(new Set<number>());
  const pending = useRef(new Set<number>());
  const ready = useRef(false);
  // False when the count request failed: the list then grows a page at a time while pages come back full.
  const countKnown = useRef(true);
  const placeholders = useCallback(
    (from: number, length: number) =>
      Array.from({length: Math.max(0, length)}, (_, index) =>
        typeof virtualItem === 'function'
          ? (virtualItem as (data: {index: number}) => TVirtualItem)({index: from + index})
          : (virtualItem as TVirtualItem),
      ),
    [virtualItem],
  );
  useEffect(() => {
    const current = ++generation.current;
    loaded.current = new Set();
    pending.current = new Set();
    ready.current = false;
    countKnown.current = true;
    setError(null);
    setIsLoadingPage(false);
    setItems([]);
    if (skip) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    // A failed count must not hide the first page: the endpoint behind it can fail on its own.
    const count = getItemsCount().then(
      (value) => (Number.isFinite(value) ? value : null),
      () => null,
    );
    void Promise.all([getItems({pageNumber: 1, pageSize}), count])
      .then(([first, count]) => {
        if (current !== generation.current) return;
        countKnown.current = count != null;
        const remaining = count != null ? count - first.length : first.length === pageSize ? pageSize : 0;
        loaded.current.add(1);
        ready.current = true;
        setItems([...first, ...placeholders(first.length, remaining)]);
        setLoadingCount((count) => count + 1);
      })
      .catch((error) => {
        if (current === generation.current) {
          setError(error);
          setItems([]);
        }
      })
      .finally(() => {
        if (current === generation.current) setIsLoading(false);
      });
    return () => {
      generation.current++;
    };
  }, [getItems, getItemsCount, pageSize, skip, placeholders]);
  const loadPage = useCallback(
    async (pageNumber: number) => {
      if (!ready.current || loaded.current.has(pageNumber) || pending.current.has(pageNumber)) return;
      const current = generation.current;
      pending.current.add(pageNumber);
      setIsLoadingPage(true);
      try {
        const page = await getItems({pageNumber, pageSize});
        if (current !== generation.current) return;
        loaded.current.add(pageNumber);
        setError(null);
        const start = (pageNumber - 1) * pageSize;
        setItems((items) =>
          countKnown.current
            ? [...items.slice(0, start), ...page, ...items.slice(start + page.length)]
            : // Without a count, a full page means there may be more: keep one page of placeholders after it.
              [
                ...items.slice(0, start),
                ...page,
                ...placeholders(start + page.length, page.length === pageSize ? pageSize : 0),
              ],
        );
      } catch (error) {
        if (current === generation.current) setError(error);
      } finally {
        if (current === generation.current) {
          pending.current.delete(pageNumber);
          setIsLoadingPage(pending.current.size > 0);
        }
      }
    },
    [getItems, pageSize, placeholders],
  );
  const onRangeChange = useCallback(
    ({startIndex, endIndex}: VisibleRange) => {
      if (endIndex <= startIndex) return;
      const first = Math.floor(Math.max(0, startIndex) / pageSize) + 1;
      const last = Math.ceil(endIndex / pageSize);
      return Promise.all(Array.from({length: Math.max(0, last - first + 1)}, (_, index) => loadPage(first + index)));
    },
    [loadPage, pageSize],
  );
  return {items, isLoading, isLoadingPage, loadingCount, error, loadPage, onRangeChange};
}
